import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    // Skip video entirely on mobile to avoid downloading a multi-MB file on small screens
    if (window.matchMedia('(max-width: 749px)').matches) return;

    const { videoSources, videoElement } = this.refs;

    const loadVideo = () => {
      for (const source of videoSources) {
        const { videoSource } = source.dataset;
        if (videoSource) source.setAttribute('src', videoSource);
      }
      videoElement.load();
    };

    // Defer loading until the element enters the viewport
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries[0].isIntersecting) {
            loadVideo();
            obs.disconnect();
          }
        },
        { rootMargin: '200px' }
      );
      observer.observe(this);
    } else {
      loadVideo();
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
